import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  FlatList,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import Animated, { SharedValue, useAnimatedStyle } from 'react-native-reanimated'
import ReanimatedSwipeable, {
  SwipeableMethods,
} from 'react-native-gesture-handler/ReanimatedSwipeable'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useRoute, RouteProp } from '@react-navigation/native'
import { debounce } from 'lodash'
import { NavigationContainer } from '@screens/Common/containers'
import { CommonRefreshControl } from '@components'
import { Icon, Input, Text } from '@components/base'
import { ListFooter } from '@components/list/common/ListFooter'
import { conversationsListSelector } from '@screens/Nora/models/nora.selectors'
import {
  CONVERSATION_UPDATE_ERROR,
  CONVERSATIONS_LOADING_ERROR,
  CONVERSATIONS_PAGE_SIZE,
  CONVERSATIONS_SORTS,
} from '@screens/Nora/constants'
import { StyleService, useStyleSheet } from '@style/service'
import {
  ChatbotConversation,
  ChatbotConversationCollection,
  ChatbotConversationDynamicFilter,
  ChatbotConversationDynamicFilterField,
  ChatbotConversationDynamicFilterItem,
  ChatbotConversationKind,
  DynamicFilterItemOperation,
  DynamicFilterOperator,
} from '@src/types'
import { useDispatchAsync, useSnack } from '@utils'
import { useIsDarkMode } from '@config/theme'
import { AppStackParamList } from '@src/navigation/types'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { ConversationCard } from '../components/ConversationCard'
import { ConversationFilter, ConversationFilterPills } from '../components/ConversationFilterPills'
import { useNoraStreamChat } from '../context/NoraStreamChatContext'

const SEARCH_DELAY = 300

const buildDynamicFilters = (
  filter: ConversationFilter,
): ChatbotConversationDynamicFilterItem[] | undefined => {
  if (filter === 'all') {
    return undefined
  }

  let item: ChatbotConversationDynamicFilter
  if (filter === 'pinned') {
    item = {
      field: ChatbotConversationDynamicFilterField.Pinned,
      operation: DynamicFilterItemOperation.Eq,
      value: 'true',
    }
  } else if (filter === 'reports') {
    item = {
      field: ChatbotConversationDynamicFilterField.Kind,
      operation: DynamicFilterItemOperation.Eq,
      value: ChatbotConversationKind.Report,
    }
  } else {
    item = {
      field: ChatbotConversationDynamicFilterField.Tag,
      operation: DynamicFilterItemOperation.Eq,
      value: filter,
    }
  }

  return [{ operator: DynamicFilterOperator.And, items: [item] }]
}

const AnimatedTouchableOpacity = Animated.createAnimatedComponent(TouchableOpacity)

interface SwipeActionProps {
  progress: SharedValue<number>
  style: StyleProp<ViewStyle>
  accessibilityLabel: string
  onPress: () => void
  children: React.ReactNode
}

// The swipe actions are laid out behind the card even at rest, so we tie their
// opacity to the swipe progress (0 closed → 1 open). This keeps them hidden
// until the row is swiped, so they aren't revealed when the card's
// TouchableOpacity dims on press. The opacity is applied directly to the button
// (rather than a wrapper View) so it stays a direct child of Swipeable's action
// row and still stretches to the full row height.
const SwipeAction = ({
  progress,
  style,
  accessibilityLabel,
  onPress,
  children,
}: SwipeActionProps) => {
  const animatedStyle = useAnimatedStyle(() => ({ opacity: progress.value }))

  return (
    <AnimatedTouchableOpacity
      style={[style, animatedStyle]}
      accessibilityLabel={accessibilityLabel}
      onPress={onPress}
    >
      {children}
    </AnimatedTouchableOpacity>
  )
}

export const NoraConversationsScreen = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const route = useRoute<RouteProp<AppStackParamList, 'NoraConversationsScreen'>>()
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const { openConversation, setShouldReloadConversations } = useNoraStreamChat()
  const conversations = useSelector(conversationsListSelector)
  const swipeableRefs = useRef<Map<string, React.RefObject<SwipeableMethods | null>>>(new Map())
  const isDarkMode = useIsDarkMode()
  const showSnack = useSnack()

  const getSwipeableRef = (id: string) => {
    let ref = swipeableRefs.current.get(id)
    if (!ref) {
      ref = React.createRef<SwipeableMethods | null>()
      swipeableRefs.current.set(id, ref)
    }
    return ref
  }

  const [selectedFilter, setSelectedFilter] = useState<ConversationFilter>(
    (route.params?.filter as ConversationFilter | undefined) ?? 'all',
  )
  const [refreshing, setRefreshing] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  // Synchronous guard: state updates are async, so onEndReached can fire again
  // before setIsLoadingMore(true) applies and trigger a duplicate page fetch.
  const loadingMoreRef = useRef(false)

  const isSearching = searchQuery.trim().length > 0

  const loadPage = useCallback(
    async (
      pageToLoad: number,
      filter: ConversationFilter,
      query: string,
      { showLoader = true }: { showLoader?: boolean } = {},
    ) => {
      const trimmedQuery = query.trim()
      const isFirstPage = pageToLoad === 1
      const showFirstPageLoader = isFirstPage && showLoader

      if (showFirstPageLoader) {
        setIsLoading(true)
      } else if (!isFirstPage) {
        setIsLoadingMore(true)
      }

      try {
        const result = await dispatchAsync<ChatbotConversationCollection>({
          type: isFirstPage ? 'nora/loadConversationsList' : 'nora/loadMoreConversationsList',
          payload: {
            sorts: CONVERSATIONS_SORTS,
            dynamicFilters: buildDynamicFilters(filter),
            query: trimmedQuery || undefined,
            page: pageToLoad,
            perPage: CONVERSATIONS_PAGE_SIZE,
          },
        })

        setHasMore(
          result.pageCount != null
            ? pageToLoad < result.pageCount
            : result.conversations.length === CONVERSATIONS_PAGE_SIZE,
        )
      } catch {
        // Swallow so callers (initial load, infinite scroll, filter/search) don't
        // produce unhandled rejections on transient network errors.
        showSnack(CONVERSATIONS_LOADING_ERROR, null, 'error')

        // Roll the page back so a failed load-more is retried on the next scroll
        // rather than skipped (handleEndReached increments page before fetching).
        if (!isFirstPage) {
          setPage(pageToLoad - 1)
        }
      } finally {
        if (showFirstPageLoader) {
          setIsLoading(false)
        } else if (!isFirstPage) {
          setIsLoadingMore(false)
        }
      }
    },
    [dispatchAsync, showSnack],
  )

  const reload = useCallback(
    (filter: ConversationFilter, query: string, options?: { showLoader?: boolean }) => {
      setPage(1)
      return loadPage(1, filter, query, options)
    },
    [loadPage],
  )

  const debouncedReload = useMemo(() => debounce(reload, SEARCH_DELAY), [reload])

  useEffect(() => () => debouncedReload.cancel(), [debouncedReload])

  // Initial load (uses the filter passed via route params, if any). Runs once;
  // later filter/search changes are handled by handleFilterChange/handleSearchChange.
  const didInitialLoad = useRef(false)
  useEffect(() => {
    if (didInitialLoad.current) {
      return
    }
    didInitialLoad.current = true
    loadPage(1, selectedFilter, '')
  }, [loadPage, selectedFilter])

  // Drop refs for conversations that no longer exist (e.g. deleted) so the map
  // doesn't grow unbounded; FlatList virtualization means we can't rely on
  // unmount callbacks for this.
  useEffect(() => {
    const liveIds = new Set(conversations.map((conversation) => conversation.id))
    swipeableRefs.current.forEach((_, id) => {
      if (!liveIds.has(id)) {
        swipeableRefs.current.delete(id)
      }
    })
  }, [conversations])

  const closeAllSwipeables = useCallback(() => {
    swipeableRefs.current.forEach((ref) => ref.current?.close())
  }, [])

  const openAllDeleteActions = useCallback(() => {
    swipeableRefs.current.forEach((ref) => ref.current?.openRight())
  }, [])

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query)
      // Block load-more during the debounce window: page/hasMore still belong to the
      // previous query, so onEndReached would append the new query's next page onto
      // the old list. The debounced page-1 reload recomputes hasMore for the new query.
      setHasMore(false)
      debouncedReload(selectedFilter, query)
    },
    [debouncedReload, selectedFilter],
  )

  const handleFilterChange = useCallback(
    (filter: ConversationFilter) => {
      setSelectedFilter(filter)
      debouncedReload.cancel()
      reload(filter, searchQuery)
    },
    [debouncedReload, reload, searchQuery],
  )

  const handleEndReached = useCallback(() => {
    if (!hasMore || loadingMoreRef.current || refreshing) {
      return
    }

    loadingMoreRef.current = true
    const nextPage = page + 1
    setPage(nextPage)
    loadPage(nextPage, selectedFilter, searchQuery).finally(() => {
      loadingMoreRef.current = false
    })
  }, [hasMore, refreshing, page, loadPage, selectedFilter, searchQuery])

  const onRefresh = useCallback(async () => {
    setRefreshing(true)

    try {
      await reload(selectedFilter, searchQuery, { showLoader: false })
    } finally {
      setRefreshing(false)
    }
  }, [reload, selectedFilter, searchQuery])

  const handleToggleEditMode = useCallback(() => {
    setIsEditing((previous) => {
      const next = !previous

      if (next) {
        openAllDeleteActions()
      } else {
        closeAllSwipeables()
      }

      return next
    })
  }, [closeAllSwipeables, openAllDeleteActions])

  const handleDeleteConversation = useCallback(
    (conversation: ChatbotConversation) => {
      swipeableRefs.current.get(conversation.id)?.current?.close()
      dispatch({ type: 'nora/deleteChatbotConversation', payload: { id: conversation.id } })
      // Keep the Home screen's recent/pinned previews in sync on next focus.
      setShouldReloadConversations(true)
    },
    [dispatch, setShouldReloadConversations],
  )

  const handlePinConversation = useCallback(
    async (conversation: ChatbotConversation) => {
      swipeableRefs.current.get(conversation.id)?.current?.close()

      try {
        await dispatchAsync({
          type: 'nora/updateChatbotConversation',
          payload: { id: conversation.id, pinned: !conversation.pinned },
        })
      } catch {
        showSnack(CONVERSATION_UPDATE_ERROR, null, 'error')
        return
      }

      // Pinning/unpinning changes membership of the Pinned filter, so refetch to
      // drop/add the conversation rather than leaving a stale in-place row.
      if (selectedFilter === 'pinned') {
        reload(selectedFilter, searchQuery, { showLoader: false })
      }

      // Keep the Home screen's pinned preview/count in sync on next focus.
      setShouldReloadConversations(true)
    },
    [dispatchAsync, reload, selectedFilter, searchQuery, setShouldReloadConversations, showSnack],
  )

  const renderLeftActions = useCallback(
    (conversation: ChatbotConversation, progress: SharedValue<number>) => (
      <SwipeAction
        progress={progress}
        style={styles.pinAction}
        accessibilityLabel={`${conversation.pinned ? 'Unpin' : 'Pin'} conversation ${
          conversation.title
        }`}
        onPress={() => handlePinConversation(conversation)}
      >
        <Icon name="push-pin" size="24" style={styles.pinIcon} />
        <Text type="small" bold style={styles.pinLabel}>
          {conversation.pinned ? 'Unpin' : 'Pin'}
        </Text>
      </SwipeAction>
    ),
    [handlePinConversation, styles.pinAction, styles.pinIcon, styles.pinLabel],
  )

  const renderRightActions = useCallback(
    (conversation: ChatbotConversation, progress: SharedValue<number>) => (
      <SwipeAction
        progress={progress}
        style={styles.deleteAction}
        accessibilityLabel={`Delete conversation ${conversation.title}`}
        onPress={() => handleDeleteConversation(conversation)}
      >
        <Icon name="trash" size="24" style={styles.deleteIcon} />
      </SwipeAction>
    ),
    [handleDeleteConversation, styles.deleteAction, styles.deleteIcon],
  )

  useEffect(() => {
    if (!isEditing) {
      return
    }

    const animationFrame = requestAnimationFrame(() => {
      openAllDeleteActions()
    })

    return () => cancelAnimationFrame(animationFrame)
  }, [conversations, isEditing, openAllDeleteActions])

  return (
    <NavigationContainer
      title="Conversations"
      leftIcon={<Icon name="arrow-left" size="24" weight="bold" style={styles.leftIcon} />}
      rightAccessories={[
        {
          accessibilityLabel: isEditing ? 'Stop editing conversations' : 'Edit conversations',
          onPress: handleToggleEditMode,
          renderIconComponent: () => (
            <Icon name={isEditing ? 'x' : 'pencil-simple'} size="22" style={styles.editIcon} />
          ),
        },
      ]}
      style={styles.container}
      navigationBarProps={styles.container}
    >
      <View style={styles.searchContainer}>
        <Input
          placeholder="Search conversations"
          value={searchQuery}
          onChangeText={handleSearchChange}
          containerStyle={[styles.searchInput, isDarkMode && styles.searchInputDark]}
          accessoryLeft={<Icon name="magnifying-glass" size={24} />}
          accessoryRight={
            searchQuery ? (
              <TouchableOpacity
                accessibilityLabel="Clear Search"
                onPress={() => handleSearchChange('')}
              >
                <Icon name="x" size={16} />
              </TouchableOpacity>
            ) : undefined
          }
        />
      </View>
      <ConversationFilterPills
        selectedFilter={selectedFilter}
        onFilterChange={handleFilterChange}
      />
      <Text type="small" bold style={styles.recentLabel}>
        RECENT
      </Text>
      {isLoading ? (
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" {...styles.loaderColor} />
        </View>
      ) : (
        <FlatList
          data={conversations}
          keyExtractor={(item) => item.id}
          contentContainerStyle={[styles.listContent, { paddingBottom: insets.bottom }]}
          refreshControl={<CommonRefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.5}
          ListFooterComponent={
            <ListFooter isLoadingMore={isLoadingMore} isListEmpty={conversations.length === 0} />
          }
          renderItem={({ item: conversation }) => (
            <ReanimatedSwipeable
              ref={getSwipeableRef(conversation.id)}
              renderLeftActions={(progress) => renderLeftActions(conversation, progress)}
              renderRightActions={(progress) => renderRightActions(conversation, progress)}
              overshootRight={false}
            >
              <ConversationCard
                conversation={conversation}
                onPress={openConversation}
                searchQuery={isSearching ? searchQuery : undefined}
                hideChevron
                showDate
                previewLines={2}
              />
            </ReanimatedSwipeable>
          )}
          ItemSeparatorComponent={() => <View style={styles.cardSeparator} />}
          ListEmptyComponent={
            <Text type="regular" style={styles.noResults}>
              No conversations found
            </Text>
          }
        />
      )}
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    backgroundColor: 'theme.surface.base3',
  },
  editIcon: {
    marginRight: 16,
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 16,
  },
  searchInput: {
    backgroundColor: 'theme.background',
    borderWidth: 0,
    borderRadius: 14,
    height: 44,
    shadowColor: 'theme.solid.black',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 6,
    elevation: 1,
  },
  searchInputDark: {
    backgroundColor: 'theme.surface.base1',
    shadowColor: 'theme.solid.white',
  },
  listContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  cardSeparator: {
    height: 8,
  },
  loaderContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loaderColor: {
    color: 'theme.text.secondary',
  },
  noResults: {
    textAlign: 'center',
    color: 'theme.text.secondary',
    marginTop: 32,
  },
  pinAction: {
    backgroundColor: 'theme.info.base',
    justifyContent: 'center',
    alignItems: 'center',
    width: 80,
    borderRadius: 8,
    marginRight: 8,
  },
  pinIcon: {
    tintColor: 'theme.solid.white',
  },
  pinLabel: {
    color: 'theme.solid.white',
    marginTop: 2,
  },
  deleteAction: {
    backgroundColor: 'theme.error.base',
    justifyContent: 'center',
    alignItems: 'center',
    width: 80,
    borderRadius: 8,
    marginLeft: 8,
  },
  deleteIcon: {
    tintColor: 'theme.solid.white',
  },
  leftIcon: {
    marginLeft: 16,
  },
  recentLabel: {
    marginLeft: 16,
    marginTop: 12,
    marginBottom: 8,
  },
})
